import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import './App.css';

const Landing = () => {
  const navigate = useNavigate();
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Estados para funcionalidad
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Configuración anti-zoom
  useEffect(() => {
    if (typeof document !== "undefined") {
      let metaViewport = document.querySelector('meta[name="viewport"]');
      if (!metaViewport) {
        metaViewport = document.createElement("meta");
        metaViewport.name = "viewport";
        document.head.appendChild(metaViewport);
      }
      metaViewport.content =
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
    }
  }, []);

  // Scroll reset
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ESC handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowWhatsAppModal(false);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // WhatsApp functionality
  const toggleWhatsApp = () => {
    setShowWhatsAppModal(!showWhatsAppModal);
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close WhatsApp when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const modal = document.getElementById("whatsappModal");
      const button = document.querySelector(".whatsapp-button");
      if (
        modal &&
        !modal.contains(event.target) &&
        button &&
        !button.contains(event.target)
      ) {
        setShowWhatsAppModal(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const sendWhatsAppMessage = async (event) => {
    event.preventDefault();

    const submitBtn = document.getElementById("submitBtn");
    const originalHTML = submitBtn.innerHTML;
    submitBtn.innerHTML =
      '<svg width="16" height="16" style="animation: spin 1s linear infinite;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/></svg> Abriendo WhatsApp...';
    submitBtn.disabled = true;

    const formData = new FormData(event.target);
    const data = {
      nombre: formData.get("nombre"),
      email: formData.get("email"),
      empresa: formData.get("empresa") || "No especificada",
      mensaje: formData.get("mensaje"),
    };

    // Crear mensaje para WhatsApp
    const whatsappMessage = `Hola! Soy ${data.nombre} de ${data.empresa}.

Tengo una consulta sobre NOKK:
${data.mensaje}

Mi email: ${data.email}

Consulta desde landing NOKK`;

    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappNumber = "5491135932039";

    // Abrir WhatsApp en nueva pestaña
    window.open(
      `https://wa.me/${whatsappNumber}?text=${encodedMessage}`,
      "_blank"
    );

    // Limpiar formulario y cerrar chat
    setTimeout(() => {
      event.target.reset();
      setShowWhatsAppModal(false);
      submitBtn.innerHTML = originalHTML;
      submitBtn.disabled = false;
    }, 1000);
  };

  // Smooth scroll mejorado para navigation links
  const scrollToSection = (sectionId) => {
    const target = document.querySelector(sectionId);
    if (target) {
      const headerHeight = 80;
      const elementPosition = target.offsetTop - headerHeight;

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
      
      // Close mobile menu after navigation
      setMobileMenuOpen(false);
    }
  };

  // Header scroll effect mejorado
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector(".header");
      if (window.scrollY > 20) {
        header.style.background = "rgba(255, 255, 255, 0.98)";
        header.style.backdropFilter = "blur(20px)";
        header.style.boxShadow = "0 1px 30px rgba(0, 0, 0, 0.08)";
      } else {
        header.style.background = "rgba(255, 255, 255, 0.95)";
        header.style.backdropFilter = "blur(10px)";
        header.style.boxShadow = "none";
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection Observer mejorado para animaciones repetidas
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: "-20px 0px -20px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      });
    }, observerOptions);

    // Observe all fade-in elements
    document.querySelectorAll(".fade-in").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const videoRef = useRef(null);

  return (
    <>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: #1a1a1a;
          background: #ffffff;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* Variables CSS profesionales */
        :root {
          --primary: #1a1a1a;
          --primary-light: #333333;
          --accent: #2563eb;
          --accent-hover: #1d4ed8;
          --gray-50: #fafafa;
          --gray-100: #f5f5f5;
          --gray-200: #e5e5e5;
          --gray-300: #d4d4d4;
          --gray-400: #a3a3a3;
          --gray-500: #737373;
          --gray-600: #525252;
          --gray-700: #404040;
          --gray-800: #262626;
          --gray-900: #171717;
          --success: #059669;
          --border-radius: 8px;
          --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          --shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        /* Header arreglado */
        .header {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          z-index: 1000;
          border-bottom: 1px solid var(--gray-200);
          height: 80px;
          transition: all 0.3s ease;
        }

        .nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 2rem;
          max-width: 1400px;
          margin: 0 auto;
          height: 100%;
        }

        .logo-container {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-shrink: 0;
        }

        .logo-img {
          height: 32px;
          width: auto;
        }

        .nav-links {
          display: flex;
          gap: 2rem;
          align-items: center;
        }

        .nav-links a {
          text-decoration: none;
          color: var(--gray-600);
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0;
          position: relative;
        }

        .nav-links a::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--accent);
          transition: width 0.3s ease;
        }

        .nav-links a:hover {
          color: var(--gray-900);
        }

        .nav-links a:hover::after {
          width: 100%;
        }

        .nav-buttons {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-shrink: 0;
        }

        .btn-login {
          background: transparent;
          color: var(--gray-700);
          padding: 0.5rem 1rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-weight: 500;
          text-decoration: none;
          font-size: 0.8rem;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          white-space: nowrap;
        }

        .btn-login:hover {
          background: var(--gray-50);
          border-color: var(--gray-400);
        }

        .btn-register {
          background: var(--primary);
          color: white;
          padding: 0.5rem 1rem;
          border: none;
          border-radius: var(--border-radius);
          font-weight: 500;
          text-decoration: none;
          font-size: 0.8rem;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          white-space: nowrap;
        }

        .btn-register:hover {
          background: var(--primary-light);
        }

        /* Mobile menu completamente arreglado */
        .mobile-menu-button {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          transition: background 0.2s ease;
          color: var(--gray-700);
        }

        .mobile-menu-button:hover {
          background: var(--gray-100);
        }

        .mobile-menu {
          position: fixed;
          top: 80px;
          left: 0;
          right: 0;
          background: white;
          border-bottom: 1px solid var(--gray-200);
          box-shadow: var(--shadow-lg);
          z-index: 999;
          transform: translateY(-100%);
          transition: transform 0.3s ease;
          opacity: 0;
          visibility: hidden;
        }

        .mobile-menu.open {
          transform: translateY(0);
          opacity: 1;
          visibility: visible;
        }

        .mobile-nav-links {
          padding: 1.5rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .mobile-nav-links a {
          padding: 1rem 0;
          color: var(--gray-700);
          text-decoration: none;
          transition: color 0.2s ease;
          display: flex;
          align-items: center;
          gap: 1rem;
          border-bottom: 1px solid var(--gray-100);
          font-weight: 500;
          font-size: 1rem;
        }

        .mobile-nav-links a:last-child {
          border-bottom: none;
        }

        .mobile-nav-links a:hover {
          color: var(--gray-900);
        }

        .mobile-buttons {
          padding: 1.5rem 2rem;
          border-top: 1px solid var(--gray-200);
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .mobile-buttons .btn-login,
        .mobile-buttons .btn-register {
          width: 100%;
          justify-content: center;
          padding: 1rem;
          font-size: 1rem;
        }

        /* Hero Section limpio y profesional */
        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          background: linear-gradient(to bottom, #fafafa, #ffffff);
          position: relative;
          padding: 120px 0 80px;
        }

        .hero-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 2rem;
          display: grid;
          grid-template-columns: 1fr 1.1fr;
          gap: 4rem;
          align-items: center;
        }

        .hero-content {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .beta-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: var(--gray-100);
          color: var(--gray-700);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          width: fit-content;
          border: 1px solid var(--gray-200);
        }

        .hero h1 {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 700;
          line-height: 1.1;
          color: var(--gray-900);
          letter-spacing: -0.025em;
          margin: 0;
        }

        .hero-subtitle {
          font-size: clamp(1.1rem, 2vw, 1.25rem);
          color: var(--gray-600);
          font-weight: 400;
          line-height: 1.6;
          max-width: 540px;
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .btn-primary {
          background: var(--primary);
          color: white;
          padding: 1rem 2rem;
          border: none;
          border-radius: var(--border-radius);
          font-weight: 600;
          font-size: 1rem;
          text-decoration: none;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          box-shadow: var(--shadow);
        }

        .btn-primary:hover {
          background: var(--primary-light);
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        .btn-secondary {
          background: white;
          color: var(--gray-700);
          padding: 1rem 2rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-weight: 600;
          font-size: 1rem;
          text-decoration: none;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          box-shadow: var(--shadow-sm);
        }

        .btn-secondary:hover {
          background: var(--gray-50);
          border-color: var(--gray-400);
          transform: translateY(-2px);
          box-shadow: var(--shadow);
        }

        /* Dashboard mockup mejorado con efectos */
        .dashboard-mockup {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: var(--shadow-xl);
          background: white;
          border: 1px solid var(--gray-200);
          transform: perspective(1000px) rotateY(-5deg) rotateX(5deg);
          transition: all 0.5s ease;
        }

        .dashboard-mockup:hover {
          transform: perspective(1000px) rotateY(-2deg) rotateX(2deg) scale(1.02);
        }

        .dashboard-image {
          width: 100%;
          height: auto;
          display: block;
        }

        /* Floating elements para el dashboard */
        .floating-element {
          position: absolute;
          background: white;
          border-radius: 12px;
          padding: 1rem;
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--gray-200);
          animation: float 6s ease-in-out infinite;
        }

        .floating-kpi {
          top: 20%;
          right: -10%;
        }

        .floating-notification {
          bottom: 20%;
          left: -8%;
          animation: float 6s ease-in-out infinite reverse;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        /* Secciones más limpias */
        .section {
          padding: 80px 0;
        }

        .section.alt {
          background: var(--gray-50);
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .section-title {
          font-size: clamp(1.875rem, 4vw, 2.25rem);
          font-weight: 700;
          margin-bottom: 1rem;
          color: var(--gray-900);
          letter-spacing: -0.025em;
        }

        .section-subtitle {
          font-size: 1.125rem;
          color: var(--gray-600);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* Features grid más profesional */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }

        .feature-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          border: 1px solid var(--gray-200);
          transition: all 0.4s ease;
          position: relative;
        }

        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: var(--shadow-xl);
          border-color: var(--gray-300);
        }

        .feature-icon {
          width: 48px;
          height: 48px;
          background: var(--gray-900);
          border-radius: var(--border-radius);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
        }

        .feature-card h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: var(--gray-900);
        }

        .feature-card p {
          color: var(--gray-600);
          line-height: 1.6;
          font-size: 1rem;
        }

        /* Benefits section diferenciada */
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .benefit-item {
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          padding: 2rem;
          border-radius: 16px;
          border: 2px solid var(--gray-100);
          transition: all 0.4s ease;
          position: relative;
          overflow: hidden;
        }

        .benefit-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--success), var(--accent));
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .benefit-item:hover {
          transform: translateY(-6px) scale(1.02);
          box-shadow: var(--shadow-xl);
          border-color: var(--gray-200);
        }

        .benefit-item:hover::before {
          transform: scaleX(1);
        }

        .benefit-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .benefit-icon {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, var(--success), #047857);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
        }

        .benefit-item h4 {
          font-weight: 600;
          color: var(--gray-900);
          font-size: 1.1rem;
        }

        .benefit-item p {
          color: var(--gray-600);
          line-height: 1.5;
          font-size: 0.95rem;
        }

        /* Demo section */
        .demo-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: start;
        }

        .demo-text h2 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: var(--gray-900);
          letter-spacing: -0.025em;
        }

        .demo-text p {
          font-size: 1.1rem;
          color: var(--gray-600);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .demo-features {
          list-style: none;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .demo-features li {
          display: flex;
          align-items: center;
          gap: 1rem;
          color: var(--gray-700);
          font-size: 1rem;
        }

        .demo-features .icon {
          width: 24px;
          height: 24px;
          background: var(--success);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 12px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .calendly-embed {
          background: white;
          border-radius: var(--border-radius);
          border: 1px solid var(--gray-200);
          overflow: hidden;
          box-shadow: var(--shadow);
        }

        .calendly-embed iframe {
          width: 100%;
          height: 500px;
          border: none;
        }

        /* Pricing section */
        .pricing {
          background: var(--gray-50);
        }

        .pricing-card {
          background: white;
          border-radius: 12px;
          padding: 3rem 2.5rem;
          border: 1px solid var(--gray-200);
          max-width: 500px;
          margin: 0 auto;
          text-align: center;
          box-shadow: var(--shadow);
        }

        .price {
          font-size: 3rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
          letter-spacing: -0.025em;
        }

        .price-period {
          color: var(--gray-500);
          font-size: 1.1rem;
          margin-bottom: 2.5rem;
          font-weight: 500;
        }

        .price-features {
          list-style: none;
          margin-bottom: 2.5rem;
          text-align: left;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .price-features li {
          padding: 0.75rem 0;
          color: var(--gray-700);
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .price-features li::before {
          content: '✓';
          width: 20px;
          height: 20px;
          background: var(--success);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 12px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .pricing-buttons {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .pricing-note {
          font-size: 0.9rem;
          color: var(--gray-500);
          line-height: 1.5;
          padding: 1.5rem;
          background: var(--gray-50);
          border-radius: var(--border-radius);
          border-left: 4px solid var(--accent);
        }

        /* CTA Section */
        .cta-section {
          background: var(--gray-900);
          color: white;
          text-align: center;
        }

        .cta-content h2 {
          font-size: clamp(2rem, 4vw, 2.5rem);
          font-weight: 700;
          margin-bottom: 1.5rem;
          letter-spacing: -0.025em;
        }

        .cta-content p {
          font-size: 1.125rem;
          margin-bottom: 2.5rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          opacity: 0.9;
          line-height: 1.6;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-white {
          background: white;
          color: var(--gray-900);
          padding: 1rem 2rem;
          border: none;
          border-radius: var(--border-radius);
          font-weight: 600;
          font-size: 1rem;
          text-decoration: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          box-shadow: var(--shadow);
        }

        .btn-white:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow-lg);
        }

        .btn-outline {
          background: transparent;
          color: white;
          padding: 1rem 2rem;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: var(--border-radius);
          font-weight: 600;
          font-size: 1rem;
          text-decoration: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .btn-outline:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.5);
        }

        /* Footer */
        .footer {
          background: var(--gray-900);
          color: var(--gray-400);
          padding: 3rem 2rem 2rem;
          text-align: center;
        }

        .footer p {
          font-size: 0.95rem;
          margin: 0.5rem 0;
          line-height: 1.6;
        }

        .footer p:first-child {
          color: var(--gray-300);
          font-weight: 500;
        }

        /* WhatsApp Widget arreglado */
        .chat-widget {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 1000;
        }

        .whatsapp-button {
          width: 56px;
          height: 56px;
          background: #25d366;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: var(--shadow-lg);
          transition: all 0.3s ease;
          border: none;
          animation: pulse 3s infinite;
        }

        .whatsapp-button:hover {
          transform: scale(1.05);
          box-shadow: var(--shadow-xl);
        }

        .whatsapp-button svg {
          width: 28px;
          height: 28px;
          color: white;
        }

        @keyframes pulse {
          0% {
            box-shadow: var(--shadow-lg), 0 0 0 0 rgba(37, 211, 102, 0.7);
          }
          70% {
            box-shadow: var(--shadow-lg), 0 0 0 10px rgba(37, 211, 102, 0);
          }
          100% {
            box-shadow: var(--shadow-lg), 0 0 0 0 rgba(37, 211, 102, 0);
          }
        }

        .whatsapp-modal {
          position: fixed;
          bottom: 90px;
          right: 24px;
          width: 320px;
          max-width: calc(100vw - 48px);
          background: white;
          border-radius: var(--border-radius);
          box-shadow: var(--shadow-xl);
          border: 1px solid var(--gray-200);
          display: ${showWhatsAppModal ? "block" : "none"};
          z-index: 1001;
          animation: ${showWhatsAppModal ? "slideUp 0.3s ease" : "none"};
          overflow: hidden;
          max-height: 70vh;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .whatsapp-header {
          background: #25d366;
          color: white;
          padding: 1rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
        }

        .whatsapp-header svg {
          width: 24px;
          height: 24px;
        }

        .whatsapp-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .whatsapp-header p {
          font-size: 0.85rem;
          margin: 0;
          opacity: 0.9;
        }

        .close-chat {
          position: absolute;
          top: 0.75rem;
          right: 1rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          color: white;
          font-size: 1.1rem;
        }

        .close-chat:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .chat-form {
          padding: 1.5rem;
          max-height: 400px;
          overflow-y: auto;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--gray-700);
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-size: 0.9rem;
          transition: all 0.2s ease;
          font-family: inherit;
          background: white;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #25d366;
          box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .chat-submit {
          width: 100%;
          background: #25d366;
          color: white;
          padding: 0.75rem;
          border: none;
          border-radius: var(--border-radius);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }

        .chat-submit:hover {
          background: #20ba5a;
        }

        .chat-submit:disabled {
          background: var(--gray-400);
          cursor: not-allowed;
        }

        /* Animaciones más sutiles y profesionales */
        .fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Animaciones escalonadas sutiles */
        .feature-card.fade-in:nth-child(1) { transition-delay: 0.1s; }
        .feature-card.fade-in:nth-child(2) { transition-delay: 0.2s; }
        .feature-card.fade-in:nth-child(3) { transition-delay: 0.3s; }
        .feature-card.fade-in:nth-child(4) { transition-delay: 0.4s; }
        .feature-card.fade-in:nth-child(5) { transition-delay: 0.5s; }
        .feature-card.fade-in:nth-child(6) { transition-delay: 0.6s; }

        .benefit-item.fade-in:nth-child(1) { transition-delay: 0.1s; }
        .benefit-item.fade-in:nth-child(2) { transition-delay: 0.2s; }
        .benefit-item.fade-in:nth-child(3) { transition-delay: 0.3s; }
        .benefit-item.fade-in:nth-child(4) { transition-delay: 0.4s; }
        .benefit-item.fade-in:nth-child(5) { transition-delay: 0.5s; }
        .benefit-item.fade-in:nth-child(6) { transition-delay: 0.6s; }

        /* Mobile Responsive Mejorado */
        @media (max-width: 1024px) {
          .hero-container {
            grid-template-columns: 1fr;
            gap: 3rem;
            text-align: center;
          }

          .demo-content {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .floating-kpi,
          .floating-notification {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .nav-links {
            display: none;
          }

          .nav-buttons {
            display: none;
          }

          .mobile-menu-button {
            display: flex;
          }

          .mobile-menu {
            display: block;
          }

          .nav {
            padding: 0 1.5rem;
          }

          .logo-img {
            height: 28px;
          }

          .hero {
            padding: 100px 0 60px;
            min-height: auto;
          }

          .hero-container {
            padding: 0 1rem;
            gap: 2rem;
          }

          .hero h1 {
            font-size: 2.2rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .hero-buttons {
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
            max-width: 300px;
            justify-content: center;
          }

          .section {
            padding: 60px 0;
          }

          .container {
            padding: 0 1rem;
          }

          .section-header {
            margin-bottom: 2.5rem;
          }

          .section-title {
            font-size: 1.75rem;
          }

          .section-subtitle {
            font-size: 1rem;
          }

          .features-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .feature-card {
            padding: 1.5rem;
          }

          .benefits-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .benefit-item {
            padding: 1.5rem;
          }

          .pricing-card {
            padding: 2rem 1.5rem;
            margin: 0 1rem;
          }

          .demo-text {
            text-align: center;
          }

          .demo-text h2 {
            font-size: 1.6rem;
          }

          .calendly-embed iframe {
            height: 400px;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .cta-buttons .btn-white,
          .cta-buttons .btn-outline {
            width: 100%;
            max-width: 300px;
            justify-content: center;
          }

          .whatsapp-modal {
            width: calc(100vw - 32px);
            right: 16px;
            bottom: 80px;
          }

          .chat-widget {
            bottom: 16px;
            right: 16px;
          }

          .dashboard-mockup {
            transform: none;
          }

          .dashboard-mockup:hover {
            transform: scale(1.02);
          }
        }

        @media (max-width: 480px) {
          .hero h1 {
            font-size: 1.8rem;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .section-title {
            font-size: 1.5rem;
          }

          .feature-card {
            padding: 1.25rem;
          }

          .benefit-item {
            padding: 1.25rem;
          }

          .pricing-card {
            padding: 1.5rem 1rem;
          }

          .whatsapp-header {
            padding: 1rem;
          }

          .chat-form {
            padding: 1rem;
          }

          .btn-login,
          .btn-register {
            font-size: 0.75rem;
            padding: 0.4rem 0.8rem;
          }
        }

        /* Utilities */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Prevent zoom on mobile inputs */
        input, textarea, select, button {
          font-size: 16px !important;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 6px;
        }

        ::-webkit-scrollbar-track {
          background: var(--gray-100);
        }

        ::-webkit-scrollbar-thumb {
          background: var(--gray-400);
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: var(--gray-500);
        }
      `}</style>

      <div>
        {/* Header */}
        <header className="header">
          <nav className="nav">
            <div className="logo-container">
              <img
                src="https://llohruvkcdcyolmwlivx.supabase.co/storage/v1/object/public/public-assets/logonokk.png"
                alt="NOKK"
                className="logo-img"
              />
            </div>

            <div className="nav-links">
              <a href="#inicio" onClick={() => scrollToSection("#inicio")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9,22 9,12 15,12 15,22" />
                </svg>
                Inicio
              </a>
              <a href="#funcionalidades" onClick={() => scrollToSection("#funcionalidades")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v6m0 6v6" />
                  <path d="m21 12-6 0m-6 0-6 0" />
                </svg>
                Funcionalidades
              </a>
              <a href="#beneficios" onClick={() => scrollToSection("#beneficios")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2 2 7l10 5 10-5-10-5z" />
                  <path d="m2 17 10 5 10-5" />
                  <path d="m2 12 10 5 10-5" />
                </svg>
                Beneficios
              </a>
              <a href="#precio" onClick={() => scrollToSection("#precio")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 10h18" />
                </svg>
                Precio
              </a>
              <a href="#demo" onClick={() => scrollToSection("#demo")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polygon points="10 8 16 12 10 16 10 8" />
                </svg>
                Demo
              </a>
            </div>

            <div className="nav-buttons">
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowRoleSelector(true)} className="btn-login">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10,17 15,12 10,7" />
                    <line x1="15" x2="3" y1="12" y2="12" />
                  </svg>
                  Iniciar sesión
                </button>
                
                {/* Burbuja Selector de Rol */}
                {showRoleSelector && (
                  <>
                    {/* Overlay */}
                    <div
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'transparent',
                        zIndex: 9998
                      }}
                      onClick={() => setShowRoleSelector(false)}
                    />
                    
                    {/* Burbuja */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '8px',
                        background: 'rgba(255, 255, 255, 0.25)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        zIndex: 9999,
                        minWidth: '140px',
                        animation: 'bubbleIn 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Flecha hacia arriba */}
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '20px',
                        width: '0',
                        height: '0',
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderBottom: '8px solid rgba(255, 255, 255, 0.25)',
                        filter: 'drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.1))'
                      }} />
                      
                      <div style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <button
                            onClick={() => {
                              setShowRoleSelector(false);
                              navigate('/login?role=constructora');
                            }}
                            className="bubble-button"
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              background: 'rgba(238, 242, 255, 0.7)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              color: '#4338ca',
                              border: '1px solid rgba(165, 180, 252, 0.3)',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.1)'
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 21h18" />
                              <path d="M5 21V7l8-4v18" />
                              <path d="M19 21V11l-6-4" />
                            </svg>
                            Constructora
                          </button>
                          
                          <button
                            onClick={() => {
                              setShowRoleSelector(false);
                              navigate('/login?role=cliente');
                            }}
                            className="bubble-button"
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              background: 'rgba(253, 244, 255, 0.7)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              color: '#be185d',
                              border: '1px solid rgba(249, 168, 212, 0.3)',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              boxShadow: '0 2px 8px rgba(236, 72, 153, 0.1)'
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                            Cliente
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <a href="/registro-constructora" className="btn-register">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" x2="19" y1="8" y2="14" />
                  <line x1="22" x2="16" y1="11" y2="11" />
                </svg>
                Registrarse
              </a>
            </div>

            {/* Mobile menu button */}
            <button className="mobile-menu-button" onClick={toggleMobileMenu}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" x2="20" y1="12" y2="12" />
                <line x1="4" x2="20" y1="6" y2="6" />
                <line x1="4" x2="20" y1="18" y2="18" />
              </svg>
            </button>
          </nav>

          {/* Mobile menu */}
          <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <div className="mobile-nav-links">
              <a href="#inicio" onClick={() => scrollToSection("#inicio")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9,22 9,12 15,12 15,22" />
                </svg>
                Inicio
              </a>
              <a href="#funcionalidades" onClick={() => scrollToSection("#funcionalidades")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v6m0 6v6" />
                  <path d="m21 12-6 0m-6 0-6 0" />
                </svg>
                Funcionalidades
              </a>
              <a href="#beneficios" onClick={() => scrollToSection("#beneficios")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2 2 7l10 5 10-5-10-5z" />
                  <path d="m2 17 10 5 10-5" />
                  <path d="m2 12 10 5 10-5" />
                </svg>
                Beneficios
              </a>
              <a href="#precio" onClick={() => scrollToSection("#precio")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 10h18" />
                </svg>
                Precio
              </a>
              <a href="#demo" onClick={() => scrollToSection("#demo")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polygon points="10 8 16 12 10 16 10 8" />
                </svg>
                Demo
              </a>
            </div>
            <div className="mobile-buttons">
              <div style={{ position: 'relative', width: '100%' }}>
                <button 
                  onClick={() => setShowRoleSelector(!showRoleSelector)} 
                  className="btn-login"
                  style={{
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingRight: '32px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: showRoleSelector && isMobile ? '12px 12px 0 0' : '8px'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 8 }}>
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10,17 15,12 10,7" />
                    <line x1="15" x2="3" y1="12" y2="12" />
                  </svg>
                  <span style={{ pointerEvents: 'none' }}>Iniciar sesión</span>
                  <svg 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      right: 12,
                      transition: 'transform 0.3s ease',
                      transform: showRoleSelector && isMobile ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                
                {/* Selector de Rol para Mobile - Desplegable */}
                {isMobile && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'rgba(255, 255, 255, 0.98)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      borderRadius: '0 0 12px 12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderTop: 'none',
                      overflow: 'hidden',
                      maxHeight: showRoleSelector ? '200px' : '0',
                      opacity: showRoleSelector ? 1 : 0,
                      transform: showRoleSelector ? 'translateY(0)' : 'translateY(-10px)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 10000,
                      pointerEvents: showRoleSelector ? 'auto' : 'none'
                    }}
                  >
                    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <button
                        onClick={() => {
                          setShowRoleSelector(false);
                          setMobileMenuOpen(false);
                          navigate('/login?role=constructora');
                        }}
                        className="role-button-mobile"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                          color: '#4338ca',
                          border: '1px solid #c7d2fe',
                          borderRadius: '10px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: '12px',
                          boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
                          transition: 'all 0.2s ease',
                          animation: showRoleSelector ? 'slideInFromTop 0.3s ease-out 0.1s both' : 'none'
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 21h18" />
                          <path d="M5 21V7l8-4v18" />
                          <path d="M19 21V11l-6-4" />
                        </svg>
                        <span>Soy Constructora</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowRoleSelector(false);
                          setMobileMenuOpen(false);
                          navigate('/login?role=cliente');
                        }}
                        className="role-button-mobile"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)',
                          color: '#be185d',
                          border: '1px solid #f9a8d4',
                          borderRadius: '10px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: '12px',
                          boxShadow: '0 2px 8px rgba(236, 72, 153, 0.15)',
                          transition: 'all 0.2s ease',
                          animation: showRoleSelector ? 'slideInFromTop 0.3s ease-out 0.2s both' : 'none'
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <span>Soy Cliente</span>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Overlay para cerrar al hacer clic fuera */}
                {showRoleSelector && isMobile && (
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 9999,
                      background: 'transparent'
                    }}
                    onClick={() => setShowRoleSelector(false)}
                  />
                )}
              </div>
              
              <a href="/registro-constructora" className="btn-register" style={{ width: '100%' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" x2="19" y1="8" y2="14" />
                  <line x1="22" x2="16" y1="11" y2="11" />
                </svg>
                Registrarse
              </a>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section id="inicio" className="hero">
          <div className="hero-container">
            <div className="hero-content">
              <div className="beta-badge fade-in">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                VERSIÓN BETA - Acceso anticipado
              </div>

              <h1 className="fade-in">
                Sistema de gestión<br />
                integral para<br />
                constructoras
              </h1>

              <p className="hero-subtitle fade-in">
                La plataforma completa que digitaliza y optimiza toda tu operación 
                constructora en tiempo real. Control financiero, gestión de proyectos 
                y equipos en una sola aplicación.
              </p>

              <div className="hero-buttons fade-in">
                <a href="#demo" className="btn-primary" onClick={() => scrollToSection("#demo")}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                    <line x1="16" x2="16" y1="2" y2="6" />
                    <line x1="8" x2="8" y1="2" y2="6" />
                    <line x1="3" x2="21" y1="10" y2="10" />
                  </svg>
                  Agendar demo gratis
                </a>
                <a href="/registro-constructora" className="btn-secondary">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" x2="19" y1="8" y2="14" />
                    <line x1="22" x2="16" y1="11" y2="11" />
                  </svg>
                  Registrarse gratis
                </a>
              </div>
            </div>

            <div className="dashboard-mockup fade-in">
              {/* Dashboard recreado basado en tu screenshot */}
              <svg viewBox="0 0 800 600" className="dashboard-image">
                {/* Background */}
                <rect width="800" height="600" fill="#f8fafc" rx="12" />
                
                {/* Header */}
                <rect x="0" y="0" width="800" height="70" fill="white" rx="12 12 0 0" />
                <rect x="0" y="69" width="800" height="1" fill="#e2e8f0" />
                
                {/* Logo */}
                <rect x="30" y="20" width="32" height="32" fill="#3b82f6" rx="8" />
                <text x="75" y="35" fontSize="16" fill="#1e293b" fontWeight="600">Dashboard de Proyecto</text>
                <text x="75" y="50" fontSize="11" fill="#64748b">Análisis completo del estado financiero y progreso</text>
                
                {/* Currency toggle */}
                <rect x="650" y="20" width="50" height="26" fill="#f1f5f9" rx="13" />
                <text x="660" y="36" fontSize="9" fill="#64748b">$ ARS</text>
                <rect x="710" y="20" width="50" height="26" fill="#3b82f6" rx="13" />
                <text x="720" y="36" fontSize="9" fill="white" fontWeight="600">$ USD</text>
                ƒ
                {/* Project selector */}
                <rect x="30" y="90" width="350" height="40" fill="white" stroke="#e2e8f0" rx="6" />
                <rect x="40" y="100" width="20" height="20" fill="#f59e0b" rx="3" />
                <text x="70" y="115" fontSize="14" fill="#1e293b" fontWeight="600">GON - González</text>
                
                {/* KPI Cards */}
                <rect x="30" y="150" width="175" height="90" fill="white" stroke="#e2e8f0" rx="8" />
                <circle cx="55" cy="170" r="8" fill="#ef4444" opacity="0.2" />
                <text x="51" y="175" fontSize="8" fill="#ef4444">$</text>
                <text x="75" y="170" fontSize="10" fill="#64748b">TOTAL GASTADO</text>
                <text x="75" y="190" fontSize="18" fill="#1e293b" fontWeight="700">US$2,728,57</text>
                
                <rect x="220" y="150" width="175" height="90" fill="white" stroke="#e2e8f0" rx="8" />
                <circle cx="245" cy="170" r="8" fill="#10b981" opacity="0.2" />
                <text x="265" y="170" fontSize="10" fill="#64748b">METROS CÓMPUTO</text>
                <text x="265" y="190" fontSize="18" fill="#1e293b" fontWeight="700">20 m²</text>
                
                <rect x="410" y="150" width="175" height="90" fill="white" stroke="#e2e8f0" rx="8" />
                <circle cx="435" cy="170" r="8" fill="#f59e0b" opacity="0.2" />
                <text x="455" y="170" fontSize="10" fill="#64748b">TIEMPO TRANSCURRIDO</text>
                <text x="455" y="190" fontSize="18" fill="#1e293b" fontWeight="700">5 días</text>
                
                <rect x="595" y="150" width="175" height="90" fill="white" stroke="#e2e8f0" rx="8" />
                <circle cx="620" cy="170" r="8" fill="#3b82f6" opacity="0.2" />
                <text x="640" y="170" fontSize="10" fill="#64748b">FINALIZACIÓN ESTIMADA</text>
                <text x="640" y="190" fontSize="18" fill="#1e293b" fontWeight="700">10 ago 25</text>
                <text x="640" y="205" fontSize="8" fill="#64748b">1 meses y 1 días</text>
                
                {/* Bottom section - Saldo */}
                <rect x="30" y="260" width="200" height="300" fill="white" stroke="#e2e8f0" rx="8" />
                <text x="45" y="285" fontSize="14" fill="#1e293b" fontWeight="600">💳 Saldo Disponible</text>
                <text x="45" y="310" fontSize="10" fill="#64748b">PESOS ARGENTINOS</text>
                <text x="45" y="330" fontSize="20" fill="#1e293b" fontWeight="700">$0</text>
                <text x="45" y="355" fontSize="10" fill="#64748b">DÓLARES ESTADOUNIDENSES</text>
                <text x="45" y="375" fontSize="20" fill="#1e293b" fontWeight="700">US$0</text>
                
                {/* Center chart */}
                <rect x="250" y="260" width="280" height="300" fill="white" stroke="#e2e8f0" rx="8" />
                <text x="265" y="285" fontSize="14" fill="#1e293b" fontWeight="600">🎯 Costo por m² vs Presupuesto</text>
                
                {/* Donut chart */}
                <circle cx="390" cy="380" r="60" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                <circle cx="390" cy="380" r="60" fill="none" stroke="#10b981" strokeWidth="8" 
                        strokeDasharray="113 264" strokeDashoffset="0" transform="rotate(-90 390 380)" />
                
                <text x="375" y="375" fontSize="16" fill="#10b981" fontWeight="700">US$136</text>
                <text x="370" y="390" fontSize="8" fill="#64748b">POR M²</text>
                
                <text x="320" y="460" fontSize="12" fill="#10b981" fontWeight="600">28.3%</text>
                <text x="315" y="475" fontSize="8" fill="#64748b">vs Actualizado</text>
                
                <rect x="350" y="490" width="80" height="12" fill="#e2e8f0" rx="6" />
                <text x="355" y="500" fontSize="8" fill="#64748b">US$480,73 Inicial</text>
                
                <rect x="440" y="490" width="80" height="12" fill="#3b82f6" rx="6" />
                <text x="445" y="500" fontSize="8" fill="white">US$481,45 Actualizado</text>
                
                <text x="300" y="520" fontSize="8" fill="#64748b">Total invertido = 20 m² de cómputo</text>
                
                {/* Right panel */}
                <rect x="550" y="260" width="220" height="300" fill="white" stroke="#e2e8f0" rx="8" />
                <text x="565" y="285" fontSize="14" fill="#1e293b" fontWeight="600">📋 Información del Proyecto</text>
                
                <text x="565" y="320" fontSize="16" fill="#1e293b" fontWeight="700">González</text>
                <text x="565" y="340" fontSize="10" fill="#64748b">📍 Roca 962, San Isidro</text>
                
                <rect x="565" y="360" width="40" height="40" fill="#f1f5f9" rx="6" />
                <text x="575" y="375" fontSize="16" fill="#1e293b" fontWeight="700">20</text>
                <text x="568" y="388" fontSize="8" fill="#64748b">m² cómputo</text>
                
                <rect x="615" y="360" width="100" height="18" fill="#10b981" opacity="0.1" rx="4" />
                <text x="620" y="372" fontSize="9" fill="#10b981" fontWeight="600">📅 Cronograma</text>
                <text x="620" y="385" fontSize="8" fill="#64748b">1 meses y 1 días</text>
                
                <text x="565" y="420" fontSize="8" fill="#64748b">Total invertido = 20 m² de cómputo</text>
                <text x="565" y="435" fontSize="8" fill="#64748b">Progreso: 28.3% completado</text>
                
                <rect x="565" y="445" width="140" height="4" fill="#e2e8f0" rx="2" />
                <rect x="565" y="445" width="40" height="4" fill="#10b981" rx="2" />
                
                <text x="565" y="465" fontSize="8" fill="#64748b">Estado: En construcción</text>
                <circle cx="565" cy="475" r="2" fill="#f59e0b" />
                <text x="575" y="480" fontSize="8" fill="#64748b">Último reporte: Hoy 14:30</text>
              </svg>
              
              {/* Floating elements */}
              <div className="floating-element floating-kpi">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                  <div style={{width: '8px', height: '8px', background: '#10b981', borderRadius: '50%'}}></div>
                  <span style={{fontSize: '0.75rem', fontWeight: '600', color: '#1e293b'}}>Progreso en tiempo real</span>
                </div>
                <div style={{fontSize: '1.2rem', fontWeight: '700', color: '#10b981'}}>+$12,450</div>
                <div style={{fontSize: '0.7rem', color: '#64748b'}}>Últimas 24h</div>
              </div>
              
              <div className="floating-element floating-notification">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                  <div style={{width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%'}}></div>
                  <span style={{fontSize: '0.75rem', fontWeight: '600', color: '#1e293b'}}>Nueva factura</span>
                </div>
                <div style={{fontSize: '0.8rem', color: '#64748b'}}>Materiales aprobados</div>
                <div style={{fontSize: '0.7rem', color: '#64748b'}}>Hace 5 min</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="funcionalidades" className="section">
          <div className="container">
            <div className="section-header fade-in">
              <h2 className="section-title">Todo lo que necesitas en una plataforma</h2>
              <p className="section-subtitle">
                Funcionalidades diseñadas específicamente para constructoras y
                empresas del sector inmobiliario
              </p>
            </div>

            <div className="features-grid">
              <div className="feature-card fade-in">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <line x1="12" x2="12" y1="2" y2="22" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
                <h3>Gestión financiera</h3>
                <p>
                  Control de ingresos, egresos, cajas y flujo de efectivo en
                  tiempo real. Facturación digital y gestión de cobros
                  automatizada.
                </p>
              </div>

              <div className="feature-card fade-in">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9,22 9,12 15,12 15,22" />
                  </svg>
                </div>
                <h3>Gestión de proyectos</h3>
                <p>
                  Dashboard ejecutivo con KPIs, presupuestos actualizados con
                  inflación CAC, cronogramas y planificación de obras.
                </p>
              </div>

              <div className="feature-card fade-in">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <path d="m22 2-5 10-5-5 10-5z" />
                  </svg>
                </div>
                <h3>Gestión de personal</h3>
                <p>
                  KPI de trabajadores, control de rendimiento, gestión de
                  empleados y proveedores, arqueos y liquidaciones.
                </p>
              </div>

              <div className="feature-card fade-in">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v6m0 6v6" />
                    <path d="m21 12-6 0m-6 0-6 0" />
                  </svg>
                </div>
                <h3>Administración</h3>
                <p>
                  Configuración de conceptos, gestión de etapas de obra, índices
                  CAC automáticos y permisos granulares por usuario.
                </p>
              </div>

              <div className="feature-card fade-in">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <rect width="20" height="14" x="2" y="3" rx="2" />
                    <line x1="8" x2="16" y1="21" y2="21" />
                    <line x1="12" x2="12" y1="17" y2="21" />
                  </svg>
                </div>
                <h3>Multi-dispositivo</h3>
                <p>
                  Interfaz responsive que funciona perfectamente en móvil,
                  tablet y desktop. Acceso desde cualquier lugar.
                </p>
              </div>

              <div className="feature-card fade-in">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                    <circle cx="12" cy="16" r="1" />
                    <path d="m7 11 0-4a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <h3>Seguridad</h3>
                <p>
                  Sistema de permisos por rol y proyecto. Datos seguros con
                  respaldos automáticos y acceso controlado.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="beneficios" className="section alt">
          <div className="container">
            <div className="section-header fade-in">
              <h2 className="section-title">¿Por qué elegir Nokk?</h2>
              <p className="section-subtitle">
                Beneficios que transformarán tu forma de gestionar proyectos
                constructivos
              </p>
            </div>

            <div className="benefits-grid">
              <div className="benefit-item fade-in">
                <div className="benefit-header">
                  <div className="benefit-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                  </div>
                  <h4>Tiempo real</h4>
                </div>
                <p>
                  Datos actualizados automáticamente para decisiones informadas
                  al instante
                </p>
              </div>

              <div className="benefit-item fade-in">
                <div className="benefit-header">
                  <div className="benefit-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <line x1="12" x2="12" y1="2" y2="22" />
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  </div>
                  <h4>Multi-moneda</h4>
                </div>
                <p>Soporte completo para ARS, USD y conversiones automáticas</p>
              </div>

              <div className="benefit-item fade-in">
                <div className="benefit-header">
                  <div className="benefit-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M3 3v5h5" />
                      <path d="M3 5a9 9 0 0 1 9-9 9 9 0 0 1 9 9" />
                      <path d="M21 21v-5h-5" />
                      <path d="M21 19a9 9 0 0 1-9 9 9 9 0 0 1-9-9" />
                    </svg>
                  </div>
                  <h4>KPIs automatizados</h4>
                </div>
                <p>
                  Métricas clave calculadas automáticamente para mejor control
                </p>
              </div>

              <div className="benefit-item fade-in">
                <div className="benefit-header">
                  <div className="benefit-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M9 12l2 2 4-4" />
                      <circle cx="12" cy="12" r="9" />
                    </svg>
                  </div>
                  <h4>Fácil de usar</h4>
                </div>
                <p>Interfaz intuitiva diseñada para el sector construcción</p>
              </div>

              <div className="benefit-item fade-in">
                <div className="benefit-header">
                  <div className="benefit-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
                    </svg>
                  </div>
                  <h4>Escalable</h4>
                </div>
                <p>
                  Crece con tu empresa, desde proyectos pequeños hasta grandes
                  obras
                </p>
              </div>

              <div className="benefit-item fade-in">
                <div className="benefit-header">
                  <div className="benefit-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <path d="M3 3v5h5" />
                      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                      <path d="M16 16h5v5" />
                    </svg>
                  </div>
                  <h4>Actualizaciones</h4>
                </div>
                <p>
                  Mejoras continuas y nuevas funcionalidades sin costo adicional
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="precio" className="section pricing">
          <div className="container">
            <div className="section-header fade-in">
              <h2 className="section-title">Precio simple y transparente</h2>
              <p className="section-subtitle">
                Acceso completo a todas las funcionalidades por una tarifa
                mensual fija
              </p>
            </div>

              <div className="pricing-card fade-in">
              <div className="price">$45.000</div>
              <div className="price-period">por mes</div>

              <ul className="price-features">
                <li>Acceso completo a todas las funcionalidades</li>
                <li>Usuarios ilimitados</li>
                <li>Proyectos ilimitados</li>
                <li>Soporte técnico incluido</li>
                <li>Actualizaciones automáticas</li>
                <li>Respaldos diarios</li>
                <li>Configuración personalizada</li>
                <li>Demo y capacitación gratis</li>
              </ul>

              <div className="pricing-buttons">
                <a href="/registro-constructora" className="btn-primary">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" x2="19" y1="8" y2="14" />
                    <line x1="22" x2="16" y1="11" y2="11" />
                  </svg>
                  Registrarse ahora
                </a>
                <a href="#demo" className="btn-secondary" onClick={() => scrollToSection("#demo")}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                    <line x1="16" x2="16" y1="2" y2="6" />
                    <line x1="8" x2="8" y1="2" y2="6" />
                    <line x1="3" x2="21" y1="10" y2="10" />
                  </svg>
                  Agendar demo primero
                </a>
              </div>

              <div className="pricing-note">
                <strong>Versión BETA:</strong> Precio especial de lanzamiento.
                Puede contener funcionalidades en desarrollo.
              </div>
            </div>
          </div>
        </section>

        {/* Demo Section */}
        <section id="demo" className="section">
          <div className="container">
            <div className="demo-content">
              <div className="demo-text fade-in">
                <h2>Agendá tu demo en 2 minutos</h2>
                <p>
                  Descubrí cómo NOKK puede transformar la gestión de tu
                  constructora. Elegí el horario que mejor te funcione y te
                  mostramos todas las funcionalidades en vivo.
                </p>

                <ul className="demo-features">
                  <li>
                    <span className="icon">✓</span>
                    Demo personalizada de 15 minutos
                  </li>
                  <li>
                    <span className="icon">✓</span>
                    Revisión de tus necesidades específicas
                  </li>
                  <li>
                    <span className="icon">✓</span>
                    Configuración inicial gratuita
                  </li>
                  <li>
                    <span className="icon">✓</span>
                    Precio especial por ser BETA
                  </li>
                </ul>

                <p style={{fontSize: '0.95rem', color: 'var(--gray-500)', fontStyle: 'italic'}}>
                  La demo es completamente gratis y sin compromiso. Te mostramos
                  exactamente cómo funciona el sistema con datos reales.
                </p>
              </div>

              <div className="calendly-embed fade-in">
                <iframe
                  src="https://calendly.com/msonzinia/demo-nokk-15-minutos?embed_domain=localhost&embed_type=Inline&hide_landing_page_details=1&hide_gdpr_banner=1"
                  frameBorder="0"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section section">
          <div className="container">
            <div className="cta-content fade-in">
              <h2>¿Listo para digitalizar tu constructora?</h2>
              <p>
                Agendá una demo de 15 minutos y descubrí cómo NOKK puede
                transformar tu gestión empresarial
              </p>

              <div className="cta-buttons">
                <a href="/registro-constructora" className="btn-white">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" x2="19" y1="8" y2="14" />
                    <line x1="22" x2="16" y1="11" y2="11" />
                  </svg>
                  Registrarse gratis
                </a>
                <a href="#demo" className="btn-outline" onClick={() => scrollToSection("#demo")}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                    <line x1="16" x2="16" y1="2" y2="6" />
                    <line x1="8" x2="8" y1="2" y2="6" />
                    <line x1="3" x2="21" y1="10" y2="10" />
                  </svg>
                  Agendar demo gratis
                </a>
                <a href="mailto:info@nokk.com.ar" className="btn-outline">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Consultar por email
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <p>
              &copy; 2025 NOKK - Sistema de gestión para constructoras. Todos
              los derechos reservados.
            </p>
            <p>VERSIÓN BETA - En constante desarrollo y mejora</p>
          </div>
        </footer>

        {/* WhatsApp Widget */}
        <div className="chat-widget">
          <button className="whatsapp-button" onClick={toggleWhatsApp}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
            </svg>
          </button>

          <div className="whatsapp-modal" id="whatsappModal">
            <div className="whatsapp-header">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
              </svg>
              <div>
                <h3>Consultas NOKK</h3>
                <p>Te respondemos al instante</p>
              </div>
              <button className="close-chat" onClick={toggleWhatsApp}>
                ×
              </button>
            </div>
            
            <form className="chat-form" onSubmit={sendWhatsAppMessage}>
              <div className="form-group">
                <label htmlFor="userName">Nombre</label>
                <input type="text" id="userName" name="nombre" required />
              </div>
              <div className="form-group">
                <label htmlFor="userEmail">Email</label>
                <input type="email" id="userEmail" name="email" required />
              </div>
              <div className="form-group">
                <label htmlFor="userCompany">Empresa (opcional)</label>
                <input type="text" id="userCompany" name="empresa" />
              </div>
              <div className="form-group">
                <label htmlFor="userMessage">Consulta</label>
                <textarea
                  id="userMessage"
                  name="mensaje"
                  required
                  placeholder="¿Qué te gustaría saber sobre NOKK?"
                ></textarea>
              </div>
              <button type="submit" className="chat-submit" id="submitBtn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
                </svg>
                Enviar por WhatsApp
              </button>
            </form>
          </div>
        </div>
      </div>
      {/* Estilos para la animación */}
      <style>{`
        @keyframes bubbleIn {
          0% {
            opacity: 0;
            transform: translateY(-8px) scale(0.8);
            filter: blur(2px);
          }
          50% {
            opacity: 0.7;
            transform: translateY(-2px) scale(0.95);
            filter: blur(0.5px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0px);
          }
        }
        
        @keyframes bubbleOut {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0px);
          }
          50% {
            opacity: 0.7;
            transform: translateY(-2px) scale(0.95);
            filter: blur(0.5px);
          }
          100% {
            opacity: 0;
            transform: translateY(-8px) scale(0.8);
            filter: blur(2px);
          }
        }
        
        @keyframes buttonSlideIn {
          0% {
            opacity: 0;
            transform: translateX(-10px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes modalIn {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
        @keyframes slideInFromTop {
          0% {
            opacity: 0;
            transform: translateY(-8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .bubble-button {
          animation: buttonSlideIn 0.25s ease-out;
        }
        
        .bubble-button:nth-child(1) {
          animation-delay: 0.08s;
          animation-fill-mode: both;
        }
        
        .bubble-button:nth-child(2) {
          animation-delay: 0.15s;
          animation-fill-mode: both;
        }
        
        .bubble-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15) !important;
        }
        
        .bubble-button:active {
          transform: translateY(0);
        }
        
        .role-button-mobile:active {
          transform: scale(0.97);
        }
      `}</style>
      </>
    );
  };
  
  export default Landing;